import { CART_QUERY_NAME } from '@pylot-data/core-queries/cart'
import type { GraphQLError } from '@pylot-data/graphqlFetch'
import { graphqlFetch, hasUsableCustomerToken } from '@pylot-data/graphqlFetch'
import { SwrUseQueryOutput, useQuery } from '@pylot-data/hooks/use-query'
import type { Cart, CartUserInputError, Maybe } from '@pylot-data/pylotschema'
import type { RequestInit } from '@vercel/fetch'
import { useRouter } from 'next/router'
import type { SWRConfiguration } from 'swr'
import { CART_QUERY, CUSTOMER_CART_QUERY } from './graphql/cartQuery'
import { getEmptyGuestCart } from './utils/emptyGuestCart'
import { useUI } from '@corratech/pylot-ui/context'
import useSWR from 'swr'
import { FREE_SHIPPING_RULE } from './graphql/cartQuery'

export type ShippingRule = {
    name: string
    is_active: boolean
    subTotalAmount: number
    excludeCategorys: [string]
}

export const useShippingRule = (
    swrOptions?: SWRConfiguration,
    fetchOptions?: RequestInit
) => {
    const { locale } = useRouter()

    const fetchShippingRule = async () => {
        const result = await graphqlFetch<
            Record<string, never>, // variables
            { getShippingAmount: ShippingRule }
        >({
            query: FREE_SHIPPING_RULE,
            variables: {},
            fetchOptions,
            locale
        })

        return result?.data?.getShippingAmount ?? null
    }

    const { data, error, isValidating, mutate } = useSWR<ShippingRule | null>(
        'shipping-rule',
        fetchShippingRule,
        swrOptions
    )

    return {
        data,
        error,
        isValidating,
        mutate
    }
}

export type CartData = {
    data: {
        cart: Cart
        isOptimistic?: boolean
    }
    errors?: GraphQLError[]
    user_errors?: Maybe<CartUserInputError>[]
}

export const useCart = (
    swrOptions?: SWRConfiguration,
    fetchOptions?: RequestInit
): SwrUseQueryOutput<{
    cart: Cart
    isOptimistic?: boolean
}> => {
    const { locale } = useRouter()

    const { suppressCartQuery } = useUI()

    // @ts-ignore We need this override so that we can use 2 different queries for the same query key
    const cartFetcher: typeof graphqlFetch = () => {
        // getIsSignedIn mira el token guardado. Si esta vencido o rechazado,
        // graphqlFetch no lo manda y customerCart responde
        // "The request is allowed for logged in customer".
        const isSignedIn = hasUsableCustomerToken()
        // Invitado sin carro: todavía no agregó productos (el carro se crea en
        // ensureCart). No se consulta el backend; se entrega un carro vacío.
        if (!isSignedIn && !window.localStorage.getItem('cart_id')) {
            return Promise.resolve({ data: { cart: getEmptyGuestCart() } })
        }
        const variables = isSignedIn
            ? { isSignedIn: true }
            : { cartId: window.localStorage.getItem('cart_id') ?? '' }
        const selectedQuery = isSignedIn ? CUSTOMER_CART_QUERY : CART_QUERY
        const cartFetchOptions = {
            ...fetchOptions,
            method: 'POST',
            cache: 'no-store'
        } as RequestInit

        return graphqlFetch({
            query: selectedQuery,
            variables,
            fetchOptions: cartFetchOptions,
            locale
        })
    }

    return useQuery<{ cartId: string } | null, { cart: Cart }>(
        CART_QUERY_NAME,
        null,
        {
            ...swrOptions,
            onSuccess: (result, key, config) => {
                // `cart` llega null cuando el backend responde 200 con un error
                // de autenticación/autorización (token vencido), y `data` llega
                // vacío cuando suppressCartQuery cortocircuita el fetcher.
                const cartId = result?.data?.cart?.id
                if (cartId) {
                    window.localStorage.setItem('cart_id', cartId)
                }
                swrOptions?.onSuccess?.(result, key, config)
            }
        },
        fetchOptions,
        {
            // suppressCartQuery can be used to prevent the cart query when using Optimistic UI with Mutations
            // For example, when using the Optimistic UI with the AddToCart mutation, we don't want to replace
            // the optimstic cart data with an actual cart query response before the ATC mutation is complete.
            overrideFetcher: suppressCartQuery
                ? async () => {
                      return {} as any
                  }
                : cartFetcher
        }
    )
}
